import React, { useState, useRef } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Grow from '@mui/material/Grow';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { trackEvent } from './analytics';

const navGroups = [
  {
    label: 'Product',
    items: [
      { path: '/why', label: 'Why It Matters', desc: 'The case for AI monitoring' },
      { path: '/examples', label: 'Detection Examples', desc: 'Real CCTV footage — what the AI catches' },
      { path: '/reputation', label: 'Risk & Reputation', desc: 'Financial and legal exposure' },
      { path: '/use-cases', label: 'Use Cases', desc: 'Scenarios where SMCLA acts' },
      { path: '/compare', label: 'Compare', desc: 'vs. generic cameras and other solutions' },
    ],
  },
  {
    label: 'Solutions',
    items: [
      { path: '/for-staff', label: 'For Staff & Operators', desc: 'Efficiency, scheduling, and wellbeing' },
      { path: '/integrations', label: 'App Integrations', desc: 'Embed SMCLA into your daycare platform' },
      { path: '/government', label: 'Government & Ministries', desc: 'Child welfare agencies, licensing boards & regulators' },
      { path: '/camera-partners', label: 'Camera Partners', desc: 'Bundle AI safety with your camera hardware' },
      { path: '/parents', label: 'For Parents', desc: 'What parents need to know' },
      { path: '/enterprise', label: 'Enterprise', desc: 'Multi-site operators' },
      { path: '/insurance-partners', label: 'Insurance', desc: 'Risk reduction for insurers' },
    ],
  },
  {
    label: 'Resources',
    items: [
      { path: '/blog', label: 'Blog', desc: 'Case studies and insights' },
      { path: '/about', label: 'About', desc: 'Our story and mission' },
    ],
  },
];

interface HeaderProps {
  onBookDemo?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onBookDemo }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setOpenGroup(null);
      setAnchorEl(null);
    }, 150);
  };

  const handleGroupEnter = (label: string) => (e: React.MouseEvent<HTMLElement>) => {
    cancelClose();
    setOpenGroup(label);
    setAnchorEl(e.currentTarget);
  };

  const handleNavClick = (path: string) => {
    setOpenGroup(null);
    setAnchorEl(null);
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(10,10,18,0.85)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          backgroundImage: 'none',
          zIndex: 1201,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64, px: { xs: 2, md: 4 } }}>
          {/* Logo */}
          <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, textDecoration: 'none' }}>
            <img src="/logo.png" alt="Smart Media Clarity Logo" style={{ maxHeight: 40, maxWidth: 140, width: 'auto', display: 'block' }} />
          </Box>

          {/* Desktop grouped nav */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
            {navGroups.map(({ label }) => (
              <Button
                key={label}
                onMouseEnter={handleGroupEnter(label)}
                onMouseLeave={scheduleClose}
                endIcon={
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: '16px !important',
                      transition: 'transform 0.2s ease',
                      transform: openGroup === label ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                }
                sx={{
                  fontWeight: 500,
                  fontSize: 13,
                  color: openGroup === label ? '#f0f0f8' : '#9499b0',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  textTransform: 'none',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  transition: 'color 0.2s ease',
                  bgcolor: openGroup === label ? 'rgba(255,255,255,0.05)' : 'transparent',
                  '&:hover': { color: '#f0f0f8', bgcolor: 'rgba(255,255,255,0.05)' },
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          {/* Right: CTA + hamburger */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => { trackEvent('cta_click', 'Book a Demo', pathname, { location: 'header_desktop' }); if (onBookDemo) { onBookDemo(); } else { navigate('/contact'); } }}
              component="button"
              sx={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: 600,
                fontSize: 13,
                color: '#5ce1e6',
                borderColor: 'rgba(92,225,230,0.4)',
                borderRadius: 2,
                px: 2,
                py: 0.75,
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: 'rgba(92,225,230,0.08)', borderColor: '#5ce1e6' },
              }}
            >
              Book a Demo
            </Button>

            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{ display: { xs: 'flex', md: 'none' }, color: '#9499b0', p: 0.75 }}
              aria-label="Open navigation menu"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Dropdown Poppers */}
      {navGroups.map(({ label, items }) => (
        <Popper
          key={label}
          open={openGroup === label}
          anchorEl={anchorEl}
          placement="bottom-start"
          transition
          style={{ zIndex: 1300 }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} timeout={160}>
              <Paper
                elevation={0}
                sx={{
                  mt: 0.5,
                  minWidth: 240,
                  bgcolor: '#13131f',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 2,
                  boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
                  overflow: 'hidden',
                }}
              >
                {items.map(({ path, label: itemLabel, desc }) => (
                  <Box
                    key={path}
                    onClick={() => handleNavClick(path)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      transition: 'background 0.15s ease',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                      '&:hover .nav-item-label': { color: '#5ce1e6' },
                    }}
                  >
                    <Typography
                      className="nav-item-label"
                      sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', transition: 'color 0.15s ease', lineHeight: 1.3 }}
                    >
                      {itemLabel}
                    </Typography>
                    <Typography
                      sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#9499b0', mt: 0.25, lineHeight: 1.4 }}
                    >
                      {desc}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Grow>
          )}
        </Popper>
      ))}

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#0a0a12',
            borderRight: '1px solid rgba(255,255,255,0.08)',
            width: 270,
          },
        }}
      >
        {/* Drawer header */}
        <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Box component={RouterLink} to="/" onClick={() => setDrawerOpen(false)} sx={{ display: 'inline-block', textDecoration: 'none' }}>
            <img src="/logo.png" alt="SMCLA" style={{ maxHeight: 36, maxWidth: 120, width: 'auto' }} />
          </Box>
        </Box>

        {/* Grouped nav */}
        <Box sx={{ overflowY: 'auto', flex: 1, pt: 1 }}>
          {navGroups.map(({ label, items }, groupIdx) => (
            <React.Fragment key={label}>
              {groupIdx > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 3, my: 0.5 }} />}
              <List
                subheader={
                  <ListSubheader
                    disableSticky
                    sx={{
                      bgcolor: 'transparent',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'rgba(148,153,176,0.5)',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      px: 3,
                      pt: 1.5,
                      pb: 0.5,
                      lineHeight: '20px',
                    }}
                  >
                    {label}
                  </ListSubheader>
                }
                sx={{ pt: 0, pb: 0.5 }}
              >
                {items.map(({ path, label: itemLabel }) => (
                  <ListItem key={path} disablePadding>
                    <ListItemButton
                      onClick={() => handleNavClick(path)}
                      sx={{ px: 3, py: 1.25, '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}
                    >
                      <ListItemText
                        primary={itemLabel}
                        primaryTypographyProps={{
                          fontFamily: 'Inter, system-ui, sans-serif',
                          fontWeight: 500,
                          fontSize: 15,
                          color: '#9499b0',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </React.Fragment>
          ))}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 3, my: 1 }} />

        {/* Book a Demo in drawer */}
        <Box sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => { setDrawerOpen(false); trackEvent('cta_click', 'Book a Demo', pathname, { location: 'header_mobile' }); if (onBookDemo) { onBookDemo(); } else { navigate('/contact'); } }}
            sx={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: 700,
              fontSize: 14,
              bgcolor: '#5ce1e6',
              color: '#0a0a12',
              borderRadius: 2,
              py: 1.25,
              '&:hover': { bgcolor: '#4ecfd4' },
            }}
          >
            Book a Demo
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
